import { DetailRequirement } from '@cibo/core'
import { Loading, useUpdateUserDetails } from '@cibo/profile'
import {
  PROGRAM_ROUTES,
  RadioGroupFormikInput,
  ResourceDetailFeatureTaskEditorProps,
} from '@cibo/ui'
import {
  Alert,
  Collapse,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  Stack,
  Typography,
} from '@mui/material'
import { Field, Form, Formik, useFormikContext } from 'formik'
import { equals, mergeAll } from 'ramda'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath, Link } from 'react-router-dom'
import * as yup from 'yup'
import { useProgramEngagementContext } from '../../../../context'
import {
  useMultipleProgramEngagementsDetails,
  useProgramEngagementDetails,
  useProgramEngagements,
  useUpdateProgramEngagementDetails,
} from '../../../../queries'
import { UsdaGrowerDetails } from '../UsdaGrowerDetails/types'
import { BUSINESS_STRUCTURE, EqipFarmInformation, FarmInformation } from './types'

const customErrors = [
  'farmerHasExistingIndividualApplication',
  'farmerHasExistingIndividualApplicationThatCanBeModified',
]

export const EqipFarmInformationTaskEditorForm = ({
  detailRequirements,
  onUpdating,
  onError,
  onSuccess,
  subDetailRequirements,
}: EqipFarmInformationTaskEditorProps) => {
  const { t } = useTranslation('@cibo/FarmInformationTaskEditor')
  // @ts-ignore not sure how to tell our types this detail uses value: {...}
  const updateResourceDetail = useUpdateProgramEngagementDetails<EqipFarmInformation>()
  const updateUserDetails = useUpdateUserDetails<UsdaGrowerDetails>()
  const {
    engagementId,
    userRole,
    ownerName,
    userId,
    programId,
    taskEditorParams,
  } = useProgramEngagementContext()
  const details = useProgramEngagementDetails({ engagementId, detailRequirements })
  const relevantEngagementsQuery = useProgramEngagements({
    filters: [
      {
        programId: [programId],
        userId,
      },
    ],
  })
  const engagementDetails = useMultipleProgramEngagementsDetails<{
    traitId: 'eqipFarmInformation'
    result: { businessStructure: string }
    input: never
  }>({
    resourceIds: relevantEngagementsQuery.data?.items.map(item => item.id),
    detailRequirements,
  })
  const individualEngagementIds = engagementDetails.filter(engagement => {
    return engagement.data?.detail.find(detail => detail.result?.businessStructure === 'individual')
  })
  const individualEngagements = relevantEngagementsQuery.data?.items.filter(item =>
    individualEngagementIds.find(engagement => engagement.data?.resourceId === item.id)
  )

  const year = detailRequirements?.[0]?.year

  const { values, isValid, isValidating, dirty, touched } = useFormikContext()

  useEffect(() => {
    const notDirtyOrTouched = !dirty && equals(touched, {})
    if (notDirtyOrTouched || isValidating || !isValid) return
    const emptyUserSubDetails =
      subDetailRequirements
        ?.filter(a => a.dataType === 'user')
        .map(({ traitId, year }) => ({
          traitId,
          year,
          value: undefined,
        })) || []
    const emptyWorkflowSubDetails =
      subDetailRequirements
        ?.filter(a => a.dataType === 'workflow')
        .map(({ traitId, year }) => ({
          traitId,
          year,
          value: undefined,
        })) || []
    onUpdating?.()
    Promise.all([
      updateResourceDetail.mutateAsync({
        engagementId: engagementId as string,
        details: [
          {
            traitId: 'eqipFarmInformation',
            year,
            value: values,
          } as EqipFarmInformation,
          // @ts-ignore this won't be the FarmInfo detail
          ...emptyWorkflowSubDetails,
        ],
      }),
      updateUserDetails.mutateAsync({
        userId: userId?.toString() as string,
        // @ts-ignore dunno
        details: emptyUserSubDetails,
      }),
    ])
      .then(onSuccess)
      .catch(e => onError && onError(e.response))
  }, [values, isValid, isValidating, engagementId, year, dirty, touched])

  const disabled =
    details.data?.[0]?.immutable || details.isFetching || updateResourceDetail.isPending

  const readOnlyDisplay = !!taskEditorParams?.taskConfig?.rule?.params.readOnly?.find(
    (a: string) => a === 'eqipFarmInformation'
  )

  const errorReason = updateResourceDetail.error?.response?.data?.reason

  return (
    <Form>
      <Stack spacing={2}>
        <Stack>
          <Typography>{t('title')}</Typography>
          <Typography variant="caption">{t('cta')}</Typography>
        </Stack>

        {readOnlyDisplay ? (
          <Stack>
            <Typography>
              <span style={{ fontWeight: '600' }}>
                {t('businessStructureCtaReadonly', { context: userRole, name: ownerName })}
              </span>{' '}
              {/* @ts-ignore formik types don't like nested data */}
              {t('businessStructure', { context: values?.businessStructure })}
            </Typography>
          </Stack>
        ) : (
          <Stack>
            <FormLabel required>
              {t('businessStructureCta', { context: userRole, name: ownerName })}
            </FormLabel>
            <FormControl
              component="fieldset"
              variant="standard"
              error={updateResourceDetail.isError}
            >
              <Field component={RadioGroupFormikInput} row name="businessStructure">
                <FormControlLabel
                  value="entity"
                  data-testid="option-entity"
                  control={<Radio />}
                  label={t('businessStructure_entity', { context: userRole, name: ownerName })}
                  disabled={disabled}
                />
                <FormControlLabel
                  value="individual"
                  data-testid="option-individual"
                  control={<Radio />}
                  label={t('businessStructure_individual', {
                    context: userRole,
                    name: ownerName,
                  })}
                  disabled={disabled}
                />
              </Field>
            </FormControl>
          </Stack>
        )}

        <Collapse in={updateResourceDetail.isError} style={{ marginTop: '-16px' }}>
          <FormHelperText>
            {customErrors.includes(errorReason || '') ? (
              <Alert sx={{ mt: 3 }} severity="error">
                <Typography>{t(`customError_${errorReason}`)}</Typography>
                {individualEngagements && individualEngagements.length > 0 && (
                  <ul>
                    {errorReason === 'farmerHasExistingIndividualApplicationThatCanBeModified' &&
                      individualEngagements?.map(
                        engagement =>
                          !!engagement?.id && (
                            <li key={engagement.id}>
                              <Link
                                to={generatePath(PROGRAM_ROUTES.ENROLL_DETAIL, {
                                  programId: programId,
                                  workflowId: engagement.id,
                                })}
                              >
                                {engagement?.name}
                              </Link>
                            </li>
                          )
                      )}
                  </ul>
                )}
              </Alert>
            ) : (
              updateResourceDetail.error?.response?.data?.message
            )}
          </FormHelperText>
        </Collapse>
      </Stack>
    </Form>
  )
}

type EqipFarmInformationTaskEditorProps = Omit<
  ResourceDetailFeatureTaskEditorProps<EqipFarmInformation>,
  'resourceIds' | 'task' | 'engagement'
> & {
  subDetailRequirements?: DetailRequirement<any>[]
}

export const EqipFarmInformationTaskEditor = (props: EqipFarmInformationTaskEditorProps) => {
  const { t } = useTranslation('@cibo/FarmInformationTaskEditor')

  // @ts-ignore not sure how to tell our types this detail uses value: {...}
  const details = useProgramEngagementDetails<EqipFarmInformation>({
    engagementId: props.engagementId,
    detailRequirements: props.detailRequirements,
  })
  const detail = details?.data?.[0]

  const schema = useMemo(
    () =>
      yup.object({
        businessStructure: yup
          .mixed()
          .oneOf(BUSINESS_STRUCTURE)
          .required(t('required')),
      }),
    [t]
  )

  if (details.isPending) return <Loading />

  const initialValues = mergeAll([{ businessStructure: '' }, detail?.value || {}])

  return (
    <Formik<FarmInformation>
      // @ts-ignore How to allow empty strings for these initial values?
      initialValues={initialValues}
      onSubmit={() => {}}
      validationSchema={schema}
    >
      <EqipFarmInformationTaskEditorForm {...props} />
    </Formik>
  )
}
